// ABOUTME: Structured editor for a country×business-line catalog config — per-entity Enabled + scope levels,
// ABOUTME: presentation flags, and PO number. Loads the full BusinessLineConfig, edits a subset, PUTs the whole (HCP-111556).
import {Content} from '@hconnect/uikit'
import {Button} from '@hm/fundamental'
import {Dialog, DialogActions, DialogContent, DialogTitle, Paper} from '@mui/material'
import React, {useEffect, useMemo, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'

import {useAllowedHConnectConfigCountries} from '../HConnect/hooks/useAllowedHConnectConfigCountries'
import {InlineCheckbox} from '../CountryConfigurations/components/InlineCheckbox'
import {ToggleSwitch} from '../CountryConfigurations/components/ToggleSwitch'
import {UomPicker} from './components/UomPicker'
import {useCountryConfig, useUnitsOfMeasure, useUpsertCountryConfig} from './hooks/useCatalogConfig'
import {
  CATALOG_ENTITIES,
  CATALOG_SCOPE_LEVELS,
  ORDER_CHANGE_FIELDS,
  PRESENTATION_FLAGS,
  UNLOADING_METHODS,
  type BusinessLineConfig,
  type CatalogEntityKey,
  type CatalogEntitySection,
  type CatalogScopeLevel,
  type LoadSpacingScenarios,
  type LoadSpacingTruckScenario,
  type LoadSpacingUoMs,
  type PresentationFlagKey,
} from './types'

const SCOPE_LEVEL_LABELS: Record<CatalogScopeLevel, string> = {
  root: 'Root',
  orgUnit: 'Org unit',
  dispatchGroup: 'Dispatch group',
  dispatchCenter: 'Dispatch center',
  plant: 'Plant',
  customer: 'Customer',
}

const EMPTY_SECTION: CatalogEntitySection = {isEnabled: false, supportedScopeLevels: []}

const clone = (config: BusinessLineConfig): BusinessLineConfig =>
  JSON.parse(JSON.stringify(config)) as BusinessLineConfig

const DetailField: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div className="mt-3">
    <div className="mb-1 text-sm font-medium text-foreground">{label}</div>
    {children}
  </div>
)

const DetailToggle: React.FC<{
  label: string
  checked: boolean
  onChange: (value: boolean) => void
  'data-cy'?: string
}> = ({label, checked, onChange, 'data-cy': dataCy}) => (
  <div className="mt-3 flex items-center justify-between">
    <span className="text-sm text-foreground">{label}</span>
    <ToggleSwitch checked={checked} onChange={onChange} aria-label={label} data-cy={dataCy} />
  </div>
)

export const CatalogConfigurationEditor: React.FC = () => {
  const history = useHistory()
  const {countryId, businessLine} = useParams<{countryId: string; businessLine: string}>()

  const {data, isLoading, error} = useCountryConfig(countryId, businessLine)
  const upsert = useUpsertCountryConfig(countryId, businessLine)
  const {data: uomOptions = []} = useUnitsOfMeasure()

  const allowedCountries = useAllowedHConnectConfigCountries()
  const isAllowedCountry = allowedCountries.some(
    (c) => c.toUpperCase() === (countryId ?? '').toUpperCase()
  )
  useEffect(() => {
    if (!isAllowedCountry) history.replace('/catalog-configurations')
  }, [isAllowedCountry, history])

  const [draft, setDraft] = useState<BusinessLineConfig | null>(null)
  useEffect(() => {
    if (data) setDraft(clone(data))
  }, [data])

  const isDirty = useMemo(
    () => Boolean(draft && data && JSON.stringify(draft) !== JSON.stringify(data)),
    [draft, data]
  )

  const [leaveOpen, setLeaveOpen] = useState(false)
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null)

  const guardBack = () => {
    const navigateBack = () => history.push('/catalog-configurations')
    if (isDirty) {
      setPendingNav(() => navigateBack)
      setLeaveOpen(true)
    } else {
      navigateBack()
    }
  }

  const getSection = (key: CatalogEntityKey): CatalogEntitySection =>
    (draft?.[key] as CatalogEntitySection | undefined) ?? EMPTY_SECTION

  const updateSection = (key: CatalogEntityKey, patch: Partial<CatalogEntitySection>) => {
    if (!draft) return
    setDraft({...draft, [key]: {...getSection(key), ...patch}})
  }

  const toggleScopeLevel = (key: CatalogEntityKey, level: CatalogScopeLevel, checked: boolean) => {
    const current = getSection(key).supportedScopeLevels ?? []
    const next = checked ? [...current, level] : current.filter((l) => l !== level)
    updateSection(key, {supportedScopeLevels: next})
  }

  const togglePresentation = (key: PresentationFlagKey, isEnabled: boolean) => {
    if (!draft) return
    setDraft({...draft, [key]: {isEnabled}})
  }

  const updatePoNumber = (patch: Partial<BusinessLineConfig['poNumber']>) => {
    if (!draft) return
    setDraft({...draft, poNumber: {...draft.poNumber, ...patch}})
  }

  const uomIds = (section: CatalogEntitySection) => (section.availableUnitsOfMeasure ?? []).map((u) => u.id)
  const setUoms = (key: CatalogEntityKey, ids: string[]) =>
    updateSection(key, {availableUnitsOfMeasure: ids.map((id) => ({id}))})

  const toggleAllowedValue = (key: CatalogEntityKey, code: string, checked: boolean) => {
    const current = getSection(key).allowedValues ?? []
    const next = checked ? [...current, code] : current.filter((v) => v !== code)
    updateSection(key, {allowedValues: next})
  }

  const toggleField = (key: CatalogEntityKey, code: string, checked: boolean) => {
    const current = getSection(key).availableFields ?? []
    const next = checked ? [...current, code] : current.filter((v) => v !== code)
    updateSection(key, {availableFields: next})
  }

  const distanceUomIds = (section: CatalogEntitySection) =>
    (section.availableDistanceUnitsOfMeasure ?? []).map((u) => u.id)
  const setDistanceUoms = (key: CatalogEntityKey, ids: string[]) =>
    updateSection(key, {availableDistanceUnitsOfMeasure: ids.map((id) => ({id}))})

  const renderLoadSpacing = (section: CatalogEntitySection) => {
    const scenarios: LoadSpacingScenarios = section.scenarios ?? {
      singleTruck: {timeOnJobSite: false, spacing: false, flowRate: false},
      multiTruck: {timeOnJobSite: false, spacing: false, flowRate: false},
    }
    const uoms: LoadSpacingUoMs = section.availableUoMs ?? {timeOnJobSite: [], spacing: [], flowRate: []}
    const setScenario = (
      truck: 'singleTruck' | 'multiTruck',
      field: keyof LoadSpacingTruckScenario,
      value: boolean
    ) => updateSection('loadSpacing', {scenarios: {...scenarios, [truck]: {...scenarios[truck], [field]: value}}})
    const setUomSlot = (slot: keyof LoadSpacingUoMs, ids: string[]) =>
      updateSection('loadSpacing', {availableUoMs: {...uoms, [slot]: ids.map((id) => ({id}))}})
    return (
      <>
        <DetailToggle
          label="Stockpiling enabled"
          checked={section.stockpilingEnabled ?? false}
          onChange={(v) => updateSection('loadSpacing', {stockpilingEnabled: v})}
          data-cy="loadspacing-stockpiling"
        />
        {(['singleTruck', 'multiTruck'] as const).map((truck) => (
          <DetailField key={truck} label={truck === 'singleTruck' ? 'Single truck' : 'Multi truck'}>
            <div className="flex flex-wrap gap-4">
              {(['timeOnJobSite', 'spacing', 'flowRate'] as const).map((f) => (
                <label key={f} className="flex items-center gap-2 text-sm text-foreground">
                  <InlineCheckbox
                    checked={scenarios[truck][f]}
                    label={`${truck} ${f}`}
                    onChange={(v) => setScenario(truck, f, v)}
                    data-cy={`loadspacing-${truck}-${f}`}
                  />
                  {f}
                </label>
              ))}
            </div>
          </DetailField>
        ))}
        {(['timeOnJobSite', 'spacing', 'flowRate'] as const).map((slot) => (
          <DetailField key={slot} label={`Units of measure \u2014 ${slot}`}>
            <UomPicker
              options={uomOptions}
              selected={(uoms[slot] ?? []).map((u) => u.id)}
              onChange={(ids) => setUomSlot(slot, ids)}
              data-cy={`loadspacing-uom-${slot}`}
            />
          </DetailField>
        ))}
      </>
    )
  }

  const renderEntityDetails = (key: CatalogEntityKey, section: CatalogEntitySection) => {
    switch (key) {
      case 'quantity':
        return (
          <>
            <DetailToggle
              label="Full loads available"
              checked={section.fullLoadsAvailable ?? false}
              onChange={(v) => updateSection(key, {fullLoadsAvailable: v})}
              data-cy="quantity-fullloads"
            />
            <DetailToggle
              label="Load balance available"
              checked={section.loadBalanceAvailable ?? false}
              onChange={(v) => updateSection(key, {loadBalanceAvailable: v})}
              data-cy="quantity-loadbalance"
            />
            <DetailField label="Available units of measure">
              <UomPicker options={uomOptions} selected={uomIds(section)} onChange={(ids) => setUoms(key, ids)} data-cy="quantity-uom" />
            </DetailField>
          </>
        )
      case 'retarder':
      case 'overbookingAllowance':
        return (
          <DetailField label="Available units of measure">
            <UomPicker options={uomOptions} selected={uomIds(section)} onChange={(ids) => setUoms(key, ids)} data-cy={`${key}-uom`} />
          </DetailField>
        )
      case 'trucks':
        return (
          <>
            <DetailToggle
              label="Truck type selectable"
              checked={section.truckTypeSelectable ?? false}
              onChange={(v) => updateSection(key, {truckTypeSelectable: v})}
              data-cy="trucks-typeselectable"
            />
            <DetailField label="Available units of measure">
              <UomPicker options={uomOptions} selected={uomIds(section)} onChange={(ids) => setUoms(key, ids)} data-cy="trucks-uom" />
            </DetailField>
          </>
        )
      case 'unloadingMethod':
        return (
          <DetailField label="Allowed unloading methods">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3">
              {UNLOADING_METHODS.map(({code, label}) => (
                <label key={code} className="flex items-center gap-2 text-sm text-foreground">
                  <InlineCheckbox
                    checked={(section.allowedValues ?? []).includes(code)}
                    label={`${label} (${code})`}
                    onChange={(v) => toggleAllowedValue(key, code, v)}
                    data-cy={`unloading-${code}`}
                  />
                  {label} ({code})
                </label>
              ))}
            </div>
          </DetailField>
        )
      case 'orderChangeLeadTime':
        return (
          <DetailField label="Available fields">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3">
              {ORDER_CHANGE_FIELDS.map(({code, label}) => (
                <label key={code} className="flex items-center gap-2 text-sm text-foreground">
                  <InlineCheckbox
                    checked={(section.availableFields ?? []).includes(code)}
                    label={label}
                    onChange={(v) => toggleField(key, code, v)}
                    data-cy={`orderchange-field-${code}`}
                  />
                  {label}
                </label>
              ))}
            </div>
          </DetailField>
        )
      case 'orderCancellationLeadTime':
        return (
          <DetailField label="Available distance units of measure">
            <UomPicker options={uomOptions} selected={distanceUomIds(section)} onChange={(ids) => setDistanceUoms(key, ids)} data-cy="ordercancel-distuom" />
          </DetailField>
        )
      case 'loadSpacing':
        return renderLoadSpacing(section)
      default:
        return null
    }
  }

  const onSave = () => {
    if (draft) upsert.mutate(draft)
  }

  if (isLoading)
    return (
      <Content>
        <Paper elevation={4} style={{padding: '24px 32px'}}>Loading…</Paper>
      </Content>
    )
  if (error)
    return (
      <Content>
        <Paper elevation={4} style={{padding: '24px 32px'}} role="alert">
          <div className="text-error">{String((error as Error)?.message ?? error)}</div>
        </Paper>
      </Content>
    )
  if (!draft) return null

  return (
    <Content>
      <Paper elevation={4} style={{padding: '24px 32px'}} data-cy="page-catalog-configuration-editor">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <button
            type="button"
            className="mb-1 text-sm text-foreground_faded hover:underline"
            onClick={guardBack}
          >
            ← Catalog configurations
          </button>
          <h1 className="text-2xl font-semibold text-foreground">
            {countryId} · {businessLine}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {upsert.isError && (
            <span className="text-sm text-error">
              {String((upsert.error as Error)?.message ?? 'Save failed')}
            </span>
          )}
          {upsert.isSuccess && !isDirty && (
            <span className="text-sm text-success">Saved</span>
          )}
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm text-foreground_faded hover:underline disabled:opacity-50"
            onClick={() => data && setDraft(clone(data))}
            disabled={!isDirty || upsert.isLoading}
            data-cy="discard"
          >
            Discard
          </button>
          <Button onClick={onSave} disabled={!isDirty || upsert.isLoading} data-cy="save">
            {upsert.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Entities */}
      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold text-foreground">Entities</h2>
        <div className="divide-y divide-foreground_faded rounded-md border border-foreground_faded">
          {CATALOG_ENTITIES.map(({key, label}) => {
            const section = getSection(key)
            return (
              <div key={key} className="p-4" data-cy={`entity-${key}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">{label}</span>
                  <ToggleSwitch
                    checked={section.isEnabled}
                    onChange={(checked) => updateSection(key, {isEnabled: checked})}
                    aria-label={`${label} enabled`}
                    data-cy={`toggle-${key}`}
                  />
                </div>
                {section.isEnabled && (
                  <div className="mt-3 flex flex-wrap gap-4">
                    {CATALOG_SCOPE_LEVELS.map((level) => (
                      <label key={level} className="flex items-center gap-2 text-sm text-foreground">
                        <InlineCheckbox
                          checked={(section.supportedScopeLevels ?? []).includes(level)}
                          label={`${label} ${SCOPE_LEVEL_LABELS[level]}`}
                          onChange={(checked) => toggleScopeLevel(key, level, checked)}
                          data-cy={`scope-${key}-${level}`}
                        />
                        {SCOPE_LEVEL_LABELS[level]}
                      </label>
                    ))}
                  </div>
                )}
                {section.isEnabled && renderEntityDetails(key, section)}
              </div>
            )
          })}
        </div>
      </section>

      {/* Presentation flags */}
      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold text-foreground">Presentation flags</h2>
        <div className="divide-y divide-foreground_faded rounded-md border border-foreground_faded">
          {PRESENTATION_FLAGS.map(({key, label}) => (
            <div key={key} className="flex items-center justify-between p-4" data-cy={`flag-${key}`}>
              <span className="font-medium text-foreground">{label}</span>
              <ToggleSwitch
                checked={draft[key]?.isEnabled ?? false}
                onChange={(checked) => togglePresentation(key, checked)}
                aria-label={`${label} enabled`}
                data-cy={`toggle-flag-${key}`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* PO number */}
      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold text-foreground">PO number</h2>
        <div className="rounded-md border border-foreground_faded p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-medium text-foreground">Enabled</span>
            <ToggleSwitch
              checked={draft.poNumber?.enabled ?? false}
              onChange={(enabled) => updatePoNumber({enabled})}
              aria-label="PO number enabled"
              data-cy="toggle-ponumber"
            />
          </div>
          <div className="flex gap-6">
            <label className="flex flex-col text-sm text-foreground">
              Min length
              <input
                type="number"
                min={0}
                value={draft.poNumber?.minLength ?? 0}
                onChange={(e) => updatePoNumber({minLength: Number(e.target.value)})}
                className="mt-1 w-24 rounded-sm border border-foreground_faded px-2 py-1"
                data-cy="ponumber-min"
              />
            </label>
            <label className="flex flex-col text-sm text-foreground">
              Max length
              <input
                type="number"
                min={0}
                value={draft.poNumber?.maxLength ?? 0}
                onChange={(e) => updatePoNumber({maxLength: Number(e.target.value)})}
                className="mt-1 w-24 rounded-sm border border-foreground_faded px-2 py-1"
                data-cy="ponumber-max"
              />
            </label>
          </div>
        </div>
      </section>

      <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>You have unsaved edits. Leaving now will discard them.</DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={() => setLeaveOpen(false)}>
            Keep editing
          </Button>
          <Button
            onClick={() => {
              setLeaveOpen(false)
              pendingNav?.()
            }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
      </Paper>
    </Content>
  )
}
